/**
 * Day and month arithmetic over ISO date strings (`2026-09-24`), years
 * 0001–9999. No `Date`, no time zone: a calendar day never shifts for a user
 * west of UTC. Four-digit years make string order date order.
 */

/** A day with no time and no zone: `2026-09-24`. */
export type IsoDate = string;

/** 1 Monday … 7 Sunday: the numbering of `Intl.Locale`'s week info. */
export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export type IMonth = { year: number; month: number };

export const FIRST_DAY: IsoDate = "0001-01-01";
export const LAST_DAY: IsoDate = "9999-12-31";

/** Six weeks: every month fits, so the grid never changes height. */
export const GRID_SLOTS = 42;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function parse(date: IsoDate): {
  year: number;
  month: number;
  day: number;
} {
  return {
    year: Number(date.slice(0, 4)),
    month: Number(date.slice(5, 7)),
    day: Number(date.slice(8, 10)),
  };
}

const pad = (n: number, width = 2): string => String(n).padStart(width, "0");

export function isoDate(year: number, month: number, day: number): IsoDate {
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}`;
}

/** Whether `text` is a real day in `YYYY-MM-DD` form. */
export function isIsoDate(text: string): text is IsoDate {
  if (!ISO_DATE.test(text)) return false;
  const { year, month, day } = parse(text);
  return (
    year >= 1 &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= daysInMonth({ year, month })
  );
}

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function daysInMonth({ year, month }: IMonth): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

/** Days since 1970-01-01, proleptic Gregorian (Howard Hinnant's `days_from_civil`). */
function dayNumber(year: number, month: number, day: number): number {
  const y = month <= 2 ? year - 1 : year;
  const era = Math.floor(y / 400);
  const yearOfEra = y - era * 400;
  const dayOfYear =
    Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1;
  const dayOfEra =
    yearOfEra * 365 +
    Math.floor(yearOfEra / 4) -
    Math.floor(yearOfEra / 100) +
    dayOfYear;
  return era * 146097 + dayOfEra - 719468;
}

/** The inverse of `dayNumber` (Howard Hinnant's `civil_from_days`). */
function fromDayNumber(n: number): {
  year: number;
  month: number;
  day: number;
} {
  const z = n + 719468;
  const era = Math.floor(z / 146097);
  const dayOfEra = z - era * 146097;
  const yearOfEra = Math.floor(
    (dayOfEra -
      Math.floor(dayOfEra / 1460) +
      Math.floor(dayOfEra / 36524) -
      Math.floor(dayOfEra / 146096)) /
      365,
  );
  const dayOfYear =
    dayOfEra -
    (365 * yearOfEra + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100));
  const m = Math.floor((5 * dayOfYear + 2) / 153);
  const day = dayOfYear - Math.floor((153 * m + 2) / 5) + 1;
  const month = m < 10 ? m + 3 : m - 9;
  const year = yearOfEra + era * 400 + (month <= 2 ? 1 : 0);
  return { year, month, day };
}

/** 1 Monday … 7 Sunday; 1970-01-01 was a Thursday. */
export function weekday(date: IsoDate): Weekday {
  const { year, month, day } = parse(date);
  const n = dayNumber(year, month, day);
  return (((((n + 3) % 7) + 7) % 7) + 1) as Weekday;
}

/** Where `date` stands in a week that starts on `firstDay`: 0 to 6. */
export function placeInWeek(date: IsoDate, firstDay: Weekday): number {
  return (weekday(date) - firstDay + 7) % 7;
}

/** The weekdays in the order a week starting on `firstDay` shows them. */
export function weekdays(firstDay: Weekday): Weekday[] {
  return Array.from(
    { length: 7 },
    (_, i) => (((firstDay - 1 + i) % 7) + 1) as Weekday,
  );
}

/**
 * The month's 42 slots in weeks from `firstDay`: its days in their weekdays,
 * `null` before day 1 and after the last.
 */
export function monthGrid(
  shown: IMonth,
  firstDay: Weekday,
): (IsoDate | null)[] {
  const lead = placeInWeek(isoDate(shown.year, shown.month, 1), firstDay);
  const length = daysInMonth(shown);
  return Array.from({ length: GRID_SLOTS }, (_, slot) => {
    const day = slot - lead + 1;
    return day >= 1 && day <= length
      ? isoDate(shown.year, shown.month, day)
      : null;
  });
}

export function monthOf(date: IsoDate): IMonth {
  const { year, month } = parse(date);
  return { year, month };
}

export function firstOfMonth({ year, month }: IMonth): IsoDate {
  return isoDate(year, month, 1);
}

export function lastOfMonth(shown: IMonth): IsoDate {
  return isoDate(shown.year, shown.month, daysInMonth(shown));
}

const inYears = (year: number): boolean => year >= 1 && year <= 9999;

/** `days` later (earlier when negative); null outside 0001–9999. */
export function addDays(date: IsoDate, days: number): IsoDate | null {
  const { year, month, day } = parse(date);
  const moved = fromDayNumber(dayNumber(year, month, day) + days);
  return inYears(moved.year)
    ? isoDate(moved.year, moved.month, moved.day)
    : null;
}

/**
 * `months` later on the same day, clamped to the target month's length
 * (31 January + 1 is 28 or 29 February); null outside 0001–9999.
 */
export function addMonths(date: IsoDate, months: number): IsoDate | null {
  const { year, month, day } = parse(date);
  const index = year * 12 + month - 1 + months;
  const target = { year: Math.floor(index / 12), month: (index % 12) + 1 };
  if (!inYears(target.year)) return null;
  return isoDate(target.year, target.month, Math.min(day, daysInMonth(target)));
}

export function addYears(date: IsoDate, years: number): IsoDate | null {
  return addMonths(date, years * 12);
}

/** The same day in another month, clamped to that month's length. */
export function inMonth(date: IsoDate, target: IMonth): IsoDate {
  const { day } = parse(date);
  return isoDate(target.year, target.month, Math.min(day, daysInMonth(target)));
}

/** `date` moved into `first`–`last`, both inclusive. */
export function clamp(date: IsoDate, first: IsoDate, last: IsoDate): IsoDate {
  if (date < first) return first;
  return date > last ? last : date;
}

/** A `Date`'s day on the user's clock. */
export function isoDateOf(date: Date): IsoDate {
  return isoDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

/** Today on the user's clock. */
export function todayIso(): IsoDate {
  return isoDateOf(new Date());
}

/**
 * The day at UTC midnight, for `Intl` formatters set to `timeZone: "UTC"`.
 * `setUTCFullYear` keeps years below 100 from being read as 19xx.
 */
export function utcDateOf(date: IsoDate): Date {
  const { year, month, day } = parse(date);
  const at = new Date(0);
  at.setUTCFullYear(year, month - 1, day);
  return at;
}
